import React, { useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Box, ButtonBase, Grid, Typography } from '@material-ui/core';

import { ReactComponent as RightIcon } from '../../../../assets/img/icon/Right.svg';
import SubHeader from '../../../../components/Header/SubHeader.component';
import Loader from '../../../../components/LoaderSplash/Loader.component';
import SuraCard from '../../../../components/SuraCard';
import {
  COLLECTION_UP_TO_DATE,
  CoverageStatus,
  PaymentsTypes,
} from '../../../../constants/policies.constant';
import { useAuthDataContext } from '../../../../context/auth/auth.provider';
import { useVehiclesContext } from '../../../../context/vehicles/vehicles.provider';
import { getFormattedDate } from '../../../../helpers/getDate';
import { useProductNavigationStore } from '../../../../stores/Navigation/product-navigation.store';

const DOCUMENTS = [
  {
    link: '/documentation/document/policy/',
    title: 'Póliza',
    content: `Es el documento principal del seguro donde se detalla la cobertura.`,
    display: (vehicles: any[], status?: string, coverage?: boolean) =>
      vehicles.length < 4,
  },
  {
    link: '/documentation/document/payments_per_day/',
    title: 'Certificado de pagos al día',
    content: `Es el documento que certifica que estás al día con los pagos.`,
    display: (vehicles: any[], status?: string, coverage?: boolean) =>
      !!coverage || status === 'expired',
  },
  {
    link: '/documentation/document/certificate_of_coverage/',
    title: 'Certificado de cobertura',
    content: `Es un documento que constata que tenés la cobertura.`,
    display: (vehicles: any[], status?: string, coverage?: boolean) =>
      vehicles.length < 4,
  },
  {
    link: '/documentation/document/mercosur_certificate/',
    title: 'Certificado del mercosur',
    content: `Este documento es necesario para realizar viajes en paises del mercosur y limítrofes.`,
    display: (vehicles: any[], status?: string, coverage?: boolean) =>
      status !== 'cancelled',
  },
];

const Vehicle = () => {
  const { loading, vehicle, policy, getVehicleById, getPolicyById } =
    useVehiclesContext();
  const { id }: any = useParams();
  const history = useHistory();

  useEffect(() => {
    if (id) {
      if (vehicle.id !== Number(id)) {
        getVehicleById(id);
      }
    } else {
      history.goBack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (Object.keys(vehicle).length) {
      getPolicyById(vehicle.policy.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle]);

  const navigateBackFrom = useProductNavigationStore(
    (state) => state.navigateBackFrom
  );

  const navigate_constants = useProductNavigationStore(
    (state) => state.constants
  );

  const setLastPageVisited = useProductNavigationStore(
    (state) => state.setLastPageVisited
  );

  setLastPageVisited(navigate_constants.POLICY_INFO);

  const lastPage = useProductNavigationStore((state) => state.lastPage);
  const clientType = useProductNavigationStore((state) => state.clientType);

  const { embed } = useAuthDataContext();
  const redirectHome = () => {
    embed
      ? window.parent.postMessage({ return: 'overview' }, '*')
      : history.push('/dashboard');
  };

  const goBack = () => {
    navigateBackFrom(navigate_constants.POLICY_INFO);
    !lastPage || clientType === navigate_constants.SINGLE_POLICY
      ? redirectHome()
      : history.replace(`/documentation`);
  };

  return (
    <>
      <Box display={loading ? 'block' : 'none'}>
        <Loader action={'Cargando vehículo'}></Loader>
      </Box>

      <SubHeader
        content={
          Object.keys(vehicle).length &&
          Object.keys(policy).length && (
            <>
              <Typography variant="h6" display="inline">
                {vehicle.brand + ' ' + vehicle.model.split(' ')[0]}
              </Typography>{' '}
              <Typography display="inline">
                {vehicle.year}/{vehicle.license_plate}
              </Typography>
              <Typography variant="body2">
                Vencimiento de póliza{' '}
                {getFormattedDate(vehicle.policy.expires_at)}
              </Typography>
            </>
          )
        }
        goBack={() => goBack()}
      ></SubHeader>

      {Object.keys(vehicle).length && Object.keys(policy).length ? (
        <Box my={2}>
          <Grid container justify="center" alignItems="center">
            <Grid item xs={11}>
              <Grid container>
                <Grid item xs={12}>
                  <Box my={1}>
                    <Typography display="inline">
                      Póliza {vehicle.policy.number}
                    </Typography>
                    <Typography display="inline" color="primary">
                      {' - ' + CoverageStatus[vehicle.policy.status]}
                    </Typography>
                  </Box>
                </Grid>

                {!policy.pledge && (
                  <Grid item xs={12}>
                    <Box display="flex" flexWrap="wrap" alignItems="center">
                      <Box my={1} mr={2}>
                        <Typography variant="body2">
                          {' '}
                          Método de pago{' '}
                        </Typography>
                        <Typography
                          variant="caption"
                          style={{ fontWeight: 'bold' }}
                        >
                          {PaymentsTypes[vehicle.policy.payment_type]}
                        </Typography>
                      </Box>

                      {policy.financial.coverage && (
                        <Box my={1} mx={2}>
                          <Typography variant="body2">
                            {' '}
                            Estado de cobranza{' '}
                          </Typography>
                          <Typography
                            variant="caption"
                            style={{ fontWeight: 'bold' }}
                          >
                            {COLLECTION_UP_TO_DATE}
                          </Typography>
                        </Box>
                      )}

                      {!vehicle.policy.pledge && (
                        <Box my={1} mx={2}>
                          <Typography variant="body2">
                            {' '}
                            Próximo vencimiento de cuota{' '}
                          </Typography>
                          <Typography
                            variant="caption"
                            style={{ fontWeight: 'bold' }}
                          >
                            {vehicle.policy.financial.pending?.expiration_date
                              ? getFormattedDate(
                                  vehicle.policy.financial.pending
                                    ?.expiration_date
                                )
                              : 'No tiene vencimientos próximos'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                )}

                {/* DOCUMENTS */}
                <Grid item xs={12}>
                  <Box my={1}>
                    <Typography variant="body2"> Documentos </Typography>
                  </Box>
                  <Grid container>
                    {DOCUMENTS.map(
                      (doc: any, index: number) =>
                        doc.display(
                          policy.vehicles,
                          policy.status,
                          policy.financial.coverage
                        ) && (
                          <Grid item xs={12} sm={6} key={index}>
                            <Box
                              mr={2}
                              my={2}
                              style={{ height: 'calc(100% - 16px)' }}
                            >
                              <ButtonBase
                                component={Link}
                                to={{
                                  pathname: doc.link + vehicle.policy.id,
                                  state: { vehicle_id: vehicle.id },
                                }}
                                style={{ width: '100%', height: '100%' }}
                              >
                                <SuraCard>
                                  <Box
                                    m={2}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    width="100%"
                                  >
                                    <Box
                                      display="flex"
                                      justifyContent="space-between"
                                      alignItems="flex-start"
                                      flexDirection="column"
                                    >
                                      <Box>
                                        <Typography
                                          variant="body1"
                                          color="primary"
                                        >
                                          {doc.title}
                                        </Typography>
                                        <Typography variant="caption">
                                          {doc.content}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <Box>
                                      <RightIcon></RightIcon>
                                    </Box>
                                  </Box>
                                </SuraCard>
                              </ButtonBase>
                            </Box>
                          </Grid>
                        )
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      ) : null}
    </>
  );
};

export default Vehicle;
